import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';

function FeedbackViewer() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // folderId of expanded entry
  const addToast = useToastContext();

  useEffect(() => {
    loadEntries();
  }, []);

  async function loadEntries() {
    try {
      const data = await api.getFeedbackList();
      setEntries(data);
    } catch (e) {
      addToast('Feedback konnte nicht geladen werden', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Dieses Feedback wirklich loeschen?')) return;
    try {
      await api.deleteFeedback(id);
      addToast('Feedback geloescht', 'success');
      setEntries(prev => prev.filter(e => e.id !== id));
      if (expanded === id) setExpanded(null);
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  function toggleExpand(id) {
    setExpanded(prev => prev === id ? null : id);
  }

  if (loading) {
    return <div className="empty-state"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Feedback-Verwaltung</h2>
        <span className="text-sm text-muted">{entries.length} Eintraege</span>
      </div>

      {entries.length === 0 ? (
        <div className="empty-state">
          <div className="icon">&#128172;</div>
          <p>Noch kein Feedback eingegangen</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {entries.map(entry => (
            <div key={entry.id} className="card" style={{ padding: 0 }}>
              {/* Header row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: expanded === entry.id ? '3px solid var(--mc-coal)' : 'none',
                }}
                onClick={() => toggleExpand(entry.id)}
              >
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flex: 1 }}>
                  <span className="tag" style={{
                    background: entry.role === 'admin'
                      ? 'rgba(231,76,60,0.15)'
                      : entry.role === 'guest'
                        ? 'rgba(149,165,166,0.2)'
                        : 'rgba(46,204,113,0.15)',
                    color: entry.role === 'admin'
                      ? 'var(--mc-redstone)'
                      : entry.role === 'guest'
                        ? 'var(--text-muted)'
                        : 'var(--mc-emerald)',
                  }}>
                    {entry.role === 'admin' ? 'Admin' : entry.role === 'guest' ? 'Gast' : 'Spieler'}
                  </span>
                  <span className="fw-bold">{entry.user || 'Unbekannt'}</span>
                  <span className="text-sm text-muted" style={{ flex: 1 }}>
                    {entry.message && entry.message.length > 80
                      ? entry.message.substring(0, 80) + '...'
                      : entry.message}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span className="text-sm text-muted">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString('de-DE') : '-'}
                  </span>
                  {entry.hasScreenshot && (
                    <span title="Mit Screenshot" style={{ fontSize: 16 }}>&#128247;</span>
                  )}
                  <span style={{ fontSize: 12, transform: expanded === entry.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    &#9660;
                  </span>
                </div>
              </div>

              {/* Expanded detail */}
              {expanded === entry.id && (
                <div style={{ padding: 16 }}>
                  {/* Meta info */}
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                    {entry.page && (
                      <div>
                        <span className="text-sm text-muted">Seite: </span>
                        <span className="text-sm">{entry.page}</span>
                      </div>
                    )}
                    {entry.appVersion && (
                      <div>
                        <span className="text-sm text-muted">Version: </span>
                        <span className="text-sm">v{entry.appVersion}</span>
                      </div>
                    )}
                    {entry.buildNumber && (
                      <div>
                        <span className="text-sm text-muted">Build: </span>
                        <span className="text-sm">{entry.buildNumber}</span>
                      </div>
                    )}
                    {entry.windowSize && (
                      <div>
                        <span className="text-sm text-muted">Fenster: </span>
                        <span className="text-sm">{entry.windowSize}</span>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div style={{
                    background: 'rgba(0,0,0,0.2)',
                    border: '3px solid var(--mc-coal)',
                    padding: 16,
                    whiteSpace: 'pre-wrap',
                    marginBottom: 16,
                    lineHeight: '1.6',
                  }}>
                    {entry.message}
                  </div>

                  {/* Screenshot */}
                  {entry.hasScreenshot && (
                    <div style={{ marginBottom: 16 }}>
                      <p className="text-sm text-muted" style={{ marginBottom: 8 }}>Screenshot:</p>
                      <img
                        src={api.getFeedbackScreenshotUrl(entry.id)}
                        alt="Feedback Screenshot"
                        style={{
                          maxWidth: '100%',
                          border: '3px solid var(--mc-coal)',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(api.getFeedbackScreenshotUrl(entry.id), '_blank')}
                      />
                    </div>
                  )}

                  {/* UserAgent */}
                  {entry.userAgent && (
                    <p className="text-sm text-muted" style={{ marginBottom: 16, wordBreak: 'break-all' }}>
                      UA: {entry.userAgent}
                    </p>
                  )}

                  {/* Delete button */}
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                  >
                    Feedback loeschen
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FeedbackViewer;
